import { useMutation, useQuery } from '@apollo/client/react';
import {
  AddOutlined,
  CallMade,
  CallReceived,
  Close,
  Flag,
  TableChartOutlined,
  UnfoldLess,
  UnfoldMore,
} from '@mui/icons-material';
import { Box, Button, Chip, Grid, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { titleize } from 'inflection';
import { last, without } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';

import type { EventType, IssueType } from '../ReportComponents/DiaryEntry/CalendarView/DiaryEntryIssuesView';
import {
  checkEventOverlap,
  checkItemStatesOverlap,
  checkOverlap,
} from '../ReportComponents/DiaryEntry/CalendarView/helperMethods';
import DiaryEntryModal from '../ReportComponents/DiaryEntry/DiaryEntryModal';

import EditEventModal from '@/components/Events/EditEventModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type DeliveryAndCollectionItemState,
  DropEventDocument,
  type Event,
  EventStatusEnum,
  GetCompanyEventsDocument,
  GetDeliveryRegistersDocument,
  GetReportIssuesDocument,
  type Issue,
  IssueTypeEnum,
  PermissionActionEnum,
  PermissionEntityEnum,
  RemoveIssueDocument,
  UpdateReportIssueDocument,
} from '@/graphql/generated/graphql';
import { getInUTC, toDecimalHours } from '@/utils/dateTimeHelpers';
import { displayNotification } from '@/utils/notifications';

const TIMELINE_HOURS = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

type TimelineFilter = 'all' | 'issues' | 'events' | 'deliveries';

interface TimelineRibbonV2Props {
  issues: Issue[];
  selectedIssue: Issue | null;
  onSelectIssue: (issue: Issue) => void;
  onOpenDiaryTab: () => void;
}

const getCorporateIssueColors = (issueType: string, isSelected: boolean) => {
  if (issueType.toLowerCase() === 'external_issue') {
    return {
      bg: isSelected ? '#FDE68A' : '#FFFBEB',
      hoverBg: '#FEF3C7',
      borderLeft: '#D97706',
      border: isSelected ? '#D97706' : '#FDE68A',
      text: '#78350F',
    };
  }
  return {
    bg: isSelected ? '#DBEAFE' : '#EFF6FF',
    hoverBg: '#DBEAFE',
    borderLeft: '#1D4ED8',
    border: isSelected ? '#1D4ED8' : '#BFDBFE',
    text: '#1E3A8A',
  };
};

const TimelineRibbonV2: React.FC<TimelineRibbonV2Props> = ({
  issues,
  selectedIssue,
  onSelectIssue,
  onOpenDiaryTab,
}) => {
  const { selectedProject, selectedReport } = useContext(ReportContext);
  const { currentUser, dateTimePreference } = useContext(CurrentUserContext);
  const { can, canPerformAction } = useContext(PermissionContext);
  const { Edit, Manage, Create } = PermissionActionEnum;
  const { Reports, Events } = PermissionEntityEnum;

  const [activeFilter, setActiveFilter] = useState<TimelineFilter>('all');
  const [isTallMode, setIsTallMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);

  const rulerRef = useRef<HTMLDivElement | null>(null);
  const [cellSize, setCellSize] = useState<number>(1366 / 24);

  useEffect(() => {
    const updateWidth = () => {
      if (rulerRef.current && rulerRef.current.clientWidth > 0) {
        setCellSize(rulerRef.current.clientWidth / 24);
      }
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (rulerRef.current) {
      observer.observe(rulerRef.current);
    }
    window.addEventListener('resize', updateWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const { data: eventData } = useQuery(GetCompanyEventsDocument, {
    skip: currentUser?.defaultCompany?.id === undefined && selectedReport === null,
    variables: {
      companyId: currentUser?.defaultCompany?.id as string,
      date: selectedReport ? selectedReport.reportDate : null,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      status: without(Object.values(EventStatusEnum), EventStatusEnum.Discontinued),
    },
  });

  const { data: deliveryRegistersData } = useQuery(GetDeliveryRegistersDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !selectedProject || !selectedReport,
    variables: { projectId: selectedProject?.id as string },
  });

  const positionedIssues = checkOverlap(issues) as IssueType[];
  const positionedEvents = checkEventOverlap(eventData?.getCompanyEvents ?? []) as EventType[];

  const extractedStates =
    deliveryRegistersData?.getDeliveryRegisters?.flatMap((register) =>
      register.items.flatMap(
        (item) => last(item.itemStates.filter((state) => state.reportId === selectedReport?.id)) ?? [],
      ),
    ) ?? [];

  const positionedItemStates = checkItemStatesOverlap(extractedStates as DeliveryAndCollectionItemState[]);

  const [removeIssue] = useMutation(RemoveIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: { reportId: selectedReport?.id as string },
      },
    ],
  });

  const [updateIssue] = useMutation(UpdateReportIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: { reportId: selectedReport?.id as string },
      },
    ],
  });

  const [dropEvent] = useMutation(DropEventDocument, {
    onCompleted: () =>
      displayNotification({
        type: 'success',
        text: 'Event dropped successfully.',
      }),
    onError: (err) =>
      displayNotification({
        type: 'error',
        text: `Event could not be dropped: ${err.message}`,
      }),
  });

  const handleRemoveIssue = (issueId: string) => {
    if (!can(Manage, Reports, { projectId: selectedProject?.id })) return;
    removeIssue({ variables: { issueId } });
  };

  const handleRemoveEvent = (id: string) => {
    if (!can(Manage, Events, { projectId: selectedProject?.id })) return;
    dropEvent({
      variables: { id },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: 'Event', id }) });
        cache.gc();
      },
    });
  };

  const toggleIssueType = (issueType: IssueTypeEnum, id: string) => {
    updateIssue({
      variables: {
        reportIssue: {
          id,
          issueType,
        },
      },
    });
  };

  const visibleIssues = activeFilter === 'all' || activeFilter === 'issues' ? positionedIssues : [];
  const visibleEvents = activeFilter === 'all' || activeFilter === 'events' ? positionedEvents : [];
  const visibleStates = activeFilter === 'all' || activeFilter === 'deliveries' ? positionedItemStates : [];

  const totalVisibleTracks = visibleIssues.length + visibleEvents.length + visibleStates.length;
  const rowStride = 34;

  return (
    <Paper
      elevation={0}
      data-testid="diary-entry-view"
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Corporate Timeline Header */}
      <Box
        sx={{
          px: 2,
          py: 0.875,
          backgroundColor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Left: Title + Filter Pills */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: '#0F172A',
              letterSpacing: '0.06em',
              fontSize: '0.72rem',
            }}
          >
            24-HOUR SITE TIMELINE
          </Typography>

          <Stack direction="row" spacing={0.5}>
            {(
              [
                {
                  key: 'all',
                  label: 'All Activity',
                  count: positionedIssues.length + positionedEvents.length + positionedItemStates.length,
                },
                {
                  key: 'issues',
                  label: 'Diary Issues',
                  count: positionedIssues.length,
                },
                {
                  key: 'events',
                  label: 'Site Events',
                  count: positionedEvents.length,
                },
                {
                  key: 'deliveries',
                  label: 'Logistics',
                  count: positionedItemStates.length,
                },
              ] as const
            ).map((tab) => {
              const isActive = activeFilter === tab.key;
              return (
                <Chip
                  key={tab.key}
                  label={`${tab.label} (${tab.count})`}
                  size="small"
                  onClick={() => setActiveFilter(tab.key)}
                  sx={{
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: isActive ? 700 : 500,
                    cursor: 'pointer',
                    borderRadius: 1,
                    backgroundColor: isActive ? '#0F172A' : '#FFFFFF',
                    color: isActive ? '#FFFFFF' : '#475569',
                    border: '1px solid',
                    borderColor: isActive ? '#0F172A' : '#CBD5E1',
                    '&:hover': {
                      backgroundColor: isActive ? '#1E293B' : '#F1F5F9',
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>

        {/* Right: Legend + Table Log Shortcut + Expand */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', lg: 'flex' } }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '2px',
                  backgroundColor: '#1D4ED8',
                }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                Internal
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '2px',
                  backgroundColor: '#D97706',
                }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                External
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '2px',
                  backgroundColor: '#4F46E5',
                }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                Event
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '2px',
                  backgroundColor: '#059669',
                }}
              />
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
                Logistics
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              size="small"
              variant="text"
              startIcon={<TableChartOutlined sx={{ fontSize: 15 }} />}
              onClick={onOpenDiaryTab}
              sx={{
                height: 26,
                textTransform: 'none',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#1D4ED8',
                px: 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: '#EFF6FF' },
              }}
            >
              View Diary Table ({positionedIssues.length})
            </Button>

            {canPerformAction(Create, Events) && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddOutlined sx={{ fontSize: 14 }} />}
                onClick={() => setOpenAddModal(true)}
                sx={{
                  height: 26,
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  borderColor: '#CBD5E1',
                  color: '#0F172A',
                  backgroundColor: '#FFFFFF',
                  px: 1,
                  borderRadius: 1,
                }}
              >
                Add Entry
              </Button>
            )}

            <Tooltip title={isTallMode ? 'Compact Timeline' : 'Expand Timeline Height'}>
              <IconButton
                size="small"
                onClick={() => setIsTallMode((prev) => !prev)}
                sx={{
                  width: 26,
                  height: 26,
                  border: '1px solid #CBD5E1',
                  borderRadius: 1,
                  backgroundColor: '#FFFFFF',
                }}
              >
                {isTallMode ? (
                  <UnfoldLess sx={{ fontSize: 15, color: '#475569' }} />
                ) : (
                  <UnfoldMore sx={{ fontSize: 15, color: '#475569' }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* 24-Hour Ruler Bar */}
      <Box
        id="time"
        ref={rulerRef}
        sx={{
          width: '100%',
          height: 26,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {TIMELINE_HOURS.map((hour) => (
          <Box
            key={hour}
            sx={{
              width: 'calc(100% / 24)',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              pl: 0.5,
              borderLeft: '1px solid #E2E8F0',
              '&:first-of-type': { borderLeft: 'none' },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#64748B',
                fontVariantNumeric: 'tabular-nums',
                fontFamily: 'monospace',
              }}
            >
              {hour}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Timeline Tracks Canvas */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: isTallMode ? 220 : 108,
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: '#FFFFFF',
          transition: 'height 0.2s ease',
        }}
      >
        {/* Vertical Hour Gridlines */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            minHeight: Math.max(isTallMode ? 220 : 108, totalVisibleTracks * rowStride + 12),
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          {TIMELINE_HOURS.map((hour, i) => (
            <Box
              key={`grid-line-${hour}`}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${i * (100 / 24)}%`,
                width: '1px',
                borderLeft: i === 0 ? 'none' : '1px dashed #F1F5F9',
              }}
            />
          ))}
        </Box>

        {/* Empty State */}
        {totalVisibleTracks === 0 && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', position: 'relative', zIndex: 1 }}>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
              No timeline items for this filter on the selected report date.
            </Typography>
          </Stack>
        )}

        {/* 1. Render Diary Issues */}
        {visibleIssues.map((issue, index) => {
          const isSelected = selectedIssue?.id === issue.id;
          const colors = getCorporateIssueColors(issue.issueType, isSelected);
          const startDec = toDecimalHours(dayjs(issue.startTime));
          const endDec = toDecimalHours(dayjs(issue.endTime));
          const spanHours = Math.max(0.5, endDec - startDec);

          const tooltipContent = (
            <Box sx={{ p: 0.5, maxWidth: 280 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                  {issue.headline || 'Untitled Diary Entry'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#CBD5E1', fontFamily: 'monospace' }}>
                  {dayjs(issue.startTime).format('HH:mm')} – {dayjs(issue.endTime).format('HH:mm')}
                </Typography>
              </Stack>
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  color: '#0F172A',
                  p: 1,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  '& p': { m: 0 },
                }}
                dangerouslySetInnerHTML={{ __html: issue.description }}
              />
            </Box>
          );

          return (
            <Rnd
              key={`issue-v2-${issue.id}-${issue.startTime}-${issue.endTime}-${cellSize}`}
              style={{
                backgroundColor: colors.bg,
                border: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${colors.borderLeft}`,
                borderRadius: 4,
                color: colors.text,
                cursor: 'pointer',
                zIndex: isSelected ? 5 : 2,
              }}
              minHeight={28}
              maxHeight={28}
              disableDragging
              default={{
                x: startDec * cellSize,
                y: 6 + index * rowStride,
                width: `${Math.max(44, cellSize * spanHours)}px`,
                height: 28,
              }}
              resizeHandleStyles={{
                topLeft: { display: 'none' },
                topRight: { display: 'none' },
                bottomRight: { display: 'none' },
                bottomLeft: { display: 'none' },
                top: { display: 'none' },
                bottom: { display: 'none' },
              }}
              onResizeStop={(_, direction, _ref, delta) => {
                const incrementInterval = delta.width / (cellSize / 2);
                const minutesToAdd = (incrementInterval * 30).toFixed(2);
                updateIssue({
                  variables: {
                    reportIssue: {
                      id: issue.id,
                      ...(direction === 'left' && {
                        startTime: getInUTC(dayjs(issue.startTime).add(-parseInt(minutesToAdd, 10), 'minutes')),
                      }),
                      ...(direction === 'right' && {
                        endTime: getInUTC(dayjs(issue.endTime).add(parseInt(minutesToAdd, 10), 'minutes')),
                      }),
                    },
                  },
                });
              }}
            >
              <Tooltip title={tooltipContent} placement="top" arrow>
                <Box
                  id={`diary-entry-${issue.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (can(Edit, Reports, { projectId: selectedProject?.id })) {
                      onSelectIssue(issue);
                    }
                  }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.75,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: colors.hoverBg,
                    },
                    '&:hover .remove-issue-btn': {
                      opacity: 1,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleIssueType(
                          issue.issueType === IssueTypeEnum.InternalIssue
                            ? IssueTypeEnum.ExternalIssue
                            : IssueTypeEnum.InternalIssue,
                          issue.id,
                        );
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        flexShrink: 0,
                      }}
                    >
                      {issue.issueType === IssueTypeEnum.InternalIssue ? (
                        <CallReceived sx={{ fontSize: 11 }} />
                      ) : (
                        <CallMade sx={{ fontSize: 11 }} />
                      )}
                    </Box>
                    {issue.flagged && <Flag sx={{ fontSize: 12, color: '#DC2626', flexShrink: 0 }} />}
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        lineHeight: 1,
                      }}
                    >
                      {issue.headline || 'Diary Entry'}
                    </Typography>
                  </Stack>

                  <Box
                    className="remove-issue-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveIssue(issue.id);
                    }}
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.15s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: '#FFFFFF',
                      color: '#DC2626',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                      ml: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <Close sx={{ fontSize: 11 }} />
                  </Box>
                </Box>
              </Tooltip>
            </Rnd>
          );
        })}

        {/* 2. Render Site Events */}
        {visibleEvents.map((event, index) => {
          const eventHour = toDecimalHours(dayjs(event.occurredAt));
          const topOffset = 6 + (visibleIssues.length + index) * rowStride;

          const eventTooltip = (
            <Grid container direction="column" spacing={0.5} sx={{ p: 0.5 }}>
              <Typography variant="caption" fontWeight={700}>
                {event.title}
              </Typography>
              <Typography variant="caption">
                Occurred: {dayjs(event.occurredAt).format(dateTimePreference.time)} · {event.eventType}
              </Typography>
              {event.status && <Typography variant="caption">Status: {titleize(event.status)}</Typography>}
            </Grid>
          );

          return (
            <Tooltip key={`event-v2-${event.id}`} title={eventTooltip} placement="top" arrow>
              <Box
                id={`diary-entry-event-${event.id}`}
                onClick={() => setSelectedEvent(event)}
                sx={{
                  position: 'absolute',
                  left: `${eventHour * cellSize}px`,
                  top: `${topOffset}px`,
                  minWidth: 84,
                  maxWidth: 150,
                  height: 28,
                  px: 1,
                  backgroundColor: '#EEF2FF',
                  border: '1px solid #C7D2FE',
                  borderLeft: '3px solid #4F46E5',
                  borderRadius: 1,
                  color: '#312E81',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  zIndex: 2,
                  '&:hover': {
                    backgroundColor: '#E0E7FF',
                    zIndex: 6,
                  },
                  '&:hover .remove-event-btn': {
                    opacity: 1,
                  },
                }}
              >
                <Typography variant="caption" noWrap sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  {event.title}
                </Typography>
                <Box
                  className="remove-event-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveEvent(event.id);
                  }}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.15s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    color: '#DC2626',
                    ml: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Close sx={{ fontSize: 10 }} />
                </Box>
              </Box>
            </Tooltip>
          );
        })}

        {/* 3. Render Delivery/Collection Item States */}
        {visibleStates.map((itemState, index) => {
          const stateHour = toDecimalHours(dayjs(itemState.currentStateSetAt));
          const topOffset = 6 + (visibleIssues.length + visibleEvents.length + index) * rowStride;

          const stateTooltip = (
            <Stack spacing={0.25} sx={{ p: 0.5 }}>
              <Typography variant="caption" fontWeight={700}>
                {itemState.supplier}
              </Typography>
              <Typography variant="caption">
                State: {titleize(itemState.currentState)} at{' '}
                {dayjs(itemState.currentStateSetAt).format(dateTimePreference.time)}
              </Typography>
              {itemState.notes && <Typography variant="caption">Notes: {itemState.notes}</Typography>}
            </Stack>
          );

          return (
            <Tooltip key={`state-v2-${itemState.id}`} title={stateTooltip} placement="top" arrow>
              <Box
                id={`diary-entry-item-state-${itemState.id}`}
                sx={{
                  position: 'absolute',
                  left: `${stateHour * cellSize}px`,
                  top: `${topOffset}px`,
                  minWidth: 84,
                  maxWidth: 150,
                  height: 28,
                  px: 1,
                  backgroundColor: '#ECFDF5',
                  border: '1px solid #A7F3D0',
                  borderLeft: '3px solid #059669',
                  borderRadius: 1,
                  color: '#064E3B',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                  zIndex: 2,
                  '&:hover': {
                    backgroundColor: '#D1FAE5',
                    zIndex: 6,
                  },
                }}
              >
                <Typography variant="caption" noWrap sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  {itemState.supplier}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {selectedEvent && (
        <EditEventModal
          event={selectedEvent}
          project={selectedProject}
          reportDate={selectedReport?.reportDate}
          handleClose={() => setSelectedEvent(null)}
        />
      )}

      {openAddModal && <DiaryEntryModal handleClose={() => setOpenAddModal(false)} />}
    </Paper>
  );
};

export default TimelineRibbonV2;
